/**
 * FirebaseGoogleAdapter - Firebase Authentication with Google Provider
 *
 * This adapter implements authentication via Firebase using Google
 * as the OAuth provider.
 */

import { type User, onAuthStateChanged, type Unsubscribe } from 'firebase/auth';
import {
  auth,
  signInWithGoogle,
  signOut,
  getUserClaims,
  callSetClaimsEndpoint,
  refreshUserToken,
  UserNotAuthorizedError,
} from '../firebase';
import type {
  AuthAdapter,
  AuthUser,
  AuthProviderType,
  LoginOptions,
} from './AuthAdapter';
import { AuthError } from './AuthAdapter';

/**
 * FirebaseGoogleAdapter
 */
export class FirebaseGoogleAdapter implements AuthAdapter {
  readonly providerType: AuthProviderType = 'firebase-google';

  private currentUser: User | null = null;
  private authStateUnsubscribe: Unsubscribe | null = null;

  constructor() {
    this.authStateUnsubscribe = onAuthStateChanged(auth, (user) => {
      this.currentUser = user;
    });
  }

  dispose(): void {
    if (this.authStateUnsubscribe) {
      this.authStateUnsubscribe();
      this.authStateUnsubscribe = null;
    }
  }

  async login(_options?: LoginOptions): Promise<void> {
    try {
      const user = await signInWithGoogle();
      this.currentUser = user;

      await callSetClaimsEndpoint(user);
      await refreshUserToken(user);
    } catch (error) {
      console.error('[FirebaseGoogleAdapter] Login error:', error);

      if (error instanceof UserNotAuthorizedError) {
        await signOut();
        this.currentUser = null;
        throw new AuthError(
          error.message,
          'USER_NOT_AUTHORIZED',
          this.providerType,
          error,
        );
      }

      throw new AuthError(
        'Failed to sign in with Google',
        'LOGIN_FAILED',
        this.providerType,
        error,
      );
    }
  }

  async logout(): Promise<void> {
    try {
      await signOut();
      this.currentUser = null;
    } catch (error) {
      console.error('[FirebaseGoogleAdapter] Logout error:', error);
      throw new AuthError(
        'Failed to sign out',
        'LOGOUT_FAILED',
        this.providerType,
        error,
      );
    }
  }

  async getUser(): Promise<AuthUser | null> {
    const user = this.currentUser ?? auth.currentUser;

    if (!user) {
      return null;
    }

    try {
      const idToken = await user.getIdToken();

      return {
        id: user.uid,
        email: user.email || '',
        displayName: user.displayName || user.email || 'User',
        photoUrl: user.photoURL || undefined,
        idToken,
        accessToken: idToken,
        tokenExpiry: undefined,
      };
    } catch (error) {
      console.error('[FirebaseGoogleAdapter] getUser error:', error);
      return null;
    }
  }

  async getRoles(): Promise<string[]> {
    const user = this.currentUser ?? auth.currentUser;

    if (!user) {
      return [];
    }

    try {
      const claims = await getUserClaims(user);
      return claims.role ? [claims.role] : [];
    } catch (error) {
      console.error('[FirebaseGoogleAdapter] getRoles error:', error);
      return [];
    }
  }

  isAuthenticated(): boolean {
    return !!(this.currentUser ?? auth.currentUser);
  }

  async acquireTokenSilent(): Promise<string | null> {
    const user = this.currentUser ?? auth.currentUser;

    if (!user) {
      return null;
    }

    try {
      const token = await user.getIdToken(true);
      return token;
    } catch (error) {
      console.error('[FirebaseGoogleAdapter] acquireTokenSilent error:', error);
      return null;
    }
  }

  async handleRedirectCallback(): Promise<void> {
    // Firebase popup flow handles this automatically
  }
}

export const firebaseGoogleAdapter = new FirebaseGoogleAdapter();
